<template>
	<div class="users-page p-2">
		<p v-if="!canListAccounts" class="text-muted">Раздел «Учётные записи» доступен администраторам и назначенным ответственным.</p>
		<template v-else>
		<div class="users-header">
			<div>
				<h4 class="mb-1">Учётные записи</h4>
				<div class="text-muted small">
					Найдено: {{ totalRows }} из {{ users.length }}
					<span class="mx-1">·</span>
					действующих: {{ activeRowsCount }}
					<span class="mx-1">·</span>
					с ЛК: {{ cabinetRowsCount }}
				</div>
			</div>
			<div class="users-header__actions">
				<b-button size="sm" variant="outline-secondary" v-if="canManageDirectorySettings" @click="openSettings">
					<i class="fas fa-cog mr-1"></i>
					Настройки
				</b-button>
				<b-button size="sm" variant="success" v-if="canEditEmployees" @click="openCreateCard">
					<i class="fas fa-plus mr-1"></i>
					Создать учётную запись
				</b-button>
			</div>
		</div>

		<div class="users-toolbar">
			<b-input-group size="sm" class="users-search">
				<b-input-group-prepend is-text>
					<i class="fas fa-search"></i>
				</b-input-group-prepend>
				<b-form-input
					v-model.trim="filters.q"
					type="search"
					aria-label="Поиск учётных записей по ФИО, телефону, логину, роли или отделу"
					placeholder="Поиск по ФИО, телефону, логину, роли, отделу"
				></b-form-input>
				<b-input-group-append v-if="filters.q">
					<b-button variant="outline-secondary" title="Очистить поиск" @click="clearSearch">
						<i class="fas fa-times"></i>
					</b-button>
				</b-input-group-append>
			</b-input-group>

			<b-button
				class="users-mobile-filter-toggle"
				variant="outline-secondary"
				:aria-expanded="mobileFiltersOpen ? 'true' : 'false'"
				aria-controls="users-filter-fields"
				@click="mobileFiltersOpen = !mobileFiltersOpen"
			>
				<span><i class="fas fa-sliders-h mr-2" aria-hidden="true"></i>Фильтры и сортировка</span>
				<span>
					<b-badge v-if="activeFilterItems.length" variant="secondary" class="mr-2">{{ activeFilterItems.length }}</b-badge>
					<i :class="mobileFiltersOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
				</span>
			</b-button>
			<div id="users-filter-fields" class="users-filter-fields" :class="{'is-open': mobileFiltersOpen}">
				<label class="users-filter-field">
					<span>Статус учётной записи</span>
					<b-form-select v-model="filters.status" :options="statusFilterOptions" size="sm" aria-label="Статус учётной записи"></b-form-select>
				</label>
				<label class="users-filter-field">
					<span>Личный кабинет</span>
					<b-form-select v-model="filters.cabinet" :options="cabinetFilterOptions" size="sm" aria-label="Личный кабинет"></b-form-select>
				</label>
				<label class="users-filter-field">
					<span>Отдел</span>
					<b-form-select v-model="filters.departmentId" :options="departmentFilterOptions" size="sm" aria-label="Отдел"></b-form-select>
				</label>
				<label class="users-filter-field">
					<span>Роль</span>
					<b-form-select v-model="filters.roleId" :options="roleFilterOptions" size="sm" aria-label="Роль"></b-form-select>
				</label>
				<div class="users-mobile-sort">
					<label for="users-mobile-sort">Сортировка</label>
					<div class="d-flex">
						<b-form-select
							id="users-mobile-sort"
							:value="sortBy"
							:options="fields.filter(field => field.sortable).map(field => ({value: field.key, text: field.label}))"
							@input="onSortChanged({sortBy: $event, sortDesc})"
						></b-form-select>
						<b-button
							variant="outline-secondary"
							class="ml-2"
							:aria-label="sortDesc ? 'Сортировать по возрастанию' : 'Сортировать по убыванию'"
							:title="sortDesc ? 'Сортировать по возрастанию' : 'Сортировать по убыванию'"
							@click="onSortChanged({sortBy, sortDesc: !sortDesc})"
						><i :class="sortDesc ? 'fas fa-sort-amount-down' : 'fas fa-sort-amount-up'" aria-hidden="true"></i></b-button>
					</div>
				</div>
				<label class="users-mobile-page-size" for="users-mobile-page-size">
					На странице
					<b-form-select id="users-mobile-page-size" v-model="perPage" :options="pageOptions"></b-form-select>
				</label>
			</div>
		</div>

		<div v-if="activeFilterItems.length" class="users-active-filters">
			<span class="users-active-filters__label">
				<i class="fas fa-filter"></i>
				Включены фильтры:
			</span>
			<button
				v-for="item in activeFilterItems"
				:key="item.key"
				type="button"
				class="users-active-filter"
				:title="`Снять фильтр: ${item.text}`"
				@click="clearFilterItem(item.key)"
			>
				<span class="users-active-filter__text">{{ item.text }}</span>
				<i class="fas fa-times" aria-hidden="true"></i>
			</button>
			<button type="button" class="users-active-filters__reset" @click="resetFilters">
				Сбросить все
			</button>
		</div>

		<div class="users-table-meta">
			<div class="users-table-controls">
				<b-pagination
					v-if="totalRows > perPage"
					v-model="currentPage"
					:total-rows="totalRows"
					:per-page="perPage"
					align="fill"
					:limit="3"
					size="sm"
					class="my-0"
				></b-pagination>
				<b-form-group
					label="На странице"
					label-cols="6"
					label-size="sm"
					label-for="perPageSelect"
					class="mb-0 d-flex align-items-center users-page-size"
				>
					<b-form-select
						v-model="perPage"
						id="perPageSelect"
						size="sm"
						:options="pageOptions"
					></b-form-select>
				</b-form-group>
			</div>
		</div>

		<b-table
			class="users-table"
			responsive="lg"
			stacked="md"
			striped
			hover
			:items="visibleUsers"
			:fields="fields"
			small
			:current-page="currentPage"
			:per-page="perPage"
			v-if="showTable"
			sticky-header="80vh"
			:sort-by.sync="sortBy"
			:sort-desc.sync="sortDesc"
			:tbody-tr-class="rowClass"
			no-local-sorting
			show-empty
			empty-text="Учётные записи не найдены"
			@sort-changed="onSortChanged"
			@row-clicked="openUserCard"
		>
			<template v-slot:cell(last_name)="data">
				<div class="users-person" :data-user-id="data.item.user_id">
					<div
						class="users-avatar-mini"
						:title="data.item.full_name || userFullName(data.item)"
					>
						<img
							v-if="avatarUrl(data.item)"
							:src="avatarUrl(data.item)"
							:alt="data.item.full_name || userFullName(data.item)"
						>
						<span v-else>{{ userInitials(data.item) }}</span>
					</div>
					<div class="users-person__identity">
						<EmployeeLink :user-id="data.item.user_id" :fallback="data.item.full_name || userFullName(data.item)" compact />
						<small class="users-mobile-id">Учётная запись №{{ data.item.user_id }}</small>
					</div>
				</div>
			</template>
			<template v-slot:cell(phone)="data">
				<div class="users-contact-stack">
					<a v-if="data.item.phone" :href="phoneHref(data.item.phone)" @click.stop>
						{{ formatPhone(data.item.phone) }}
					</a>
					<span v-else class="text-muted">Телефон не указан</span>
					<small v-if="data.item.chat_id" class="text-muted">Telegram: {{ data.item.chat_id }}</small>
					<small v-if="data.item.gmail" class="text-muted">{{ data.item.gmail }}</small>
				</div>
			</template>
			<template v-slot:cell(active)="data">
				<div class="users-status-stack">
					<b-badge :variant="data.item.active ? 'success' : 'secondary'">
						{{ data.item.active ? 'Действующий' : 'Отключён' }}
					</b-badge>
					<small :class="data.item.cabinet_access_enabled ? 'text-success' : 'text-muted'">
						ЛК: {{ data.item.cabinet_access_enabled ? 'да' : 'нет' }}
					</small>
				</div>
			</template>
			<template v-slot:cell(login)="data">
				<div class="users-access-stack">
					<strong>{{ data.item.login || 'Логин не задан' }}</strong>
					<div v-if="normalizedRoles(data.item.roles).length" class="users-access-stack__roles">
						<span
							v-for="role in normalizedRoles(data.item.roles)"
							:key="role.id || role.name"
							class="users-tag users-tag--role"
						>{{ role.name }}</span>
					</div>
					<small v-else class="text-muted">Нет ролей</small>
				</div>
			</template>
			<template v-slot:cell(two_factor)="data">
				<div class="users-status-stack" title="Требуется ли одноразовый код после ввода пароля: по правам аккаунта или персональной настройке защиты.">
					<b-badge :variant="data.item.two_factor && data.item.two_factor.required ? 'success' : 'secondary'">
						{{ !data.item.two_factor || typeof data.item.two_factor.required !== 'boolean' ? 'Нет данных' : data.item.two_factor.required ? 'Включён' : 'Выключен' }}
					</b-badge>
					<small v-if="data.item.two_factor && !data.item.two_factor.policy_enabled" class="text-muted">Защита выключена для компании</small>
				</div>
			</template>
			<template v-slot:cell(departments)="data">
				<template v-if="normalizedDepartments(data.item.departments).length">
					<span
						v-for="department in normalizedDepartments(data.item.departments)"
						:key="department.id || department.name"
						class="users-tag users-tag--department"
					>{{ department.name }}</span>
				</template>
				<span v-else class="text-muted">Нет отделов</span>
			</template>
			<template v-slot:cell(actions)="data">
				<div class="users-actions">
					<b-button
						v-if="canEditEmployees"
						size="sm"
						variant="outline-success"
						title="Редактировать"
						:aria-label="`Редактировать учётную запись: ${data.item.full_name || userFullName(data.item)}`"
						@click.stop="onEdit(data)"
					>
						<i class="fas fa-pen"></i>
					</b-button>
				</div>
			</template>
		</b-table>
		<b-pagination
			v-if="totalRows > perPage"
			v-model="currentPage"
			class="users-mobile-pagination"
			:total-rows="totalRows"
			:per-page="perPage"
			:limit="3"
			align="fill"
		></b-pagination>
		<b-modal
			v-model="showImportModal"
			size="xl"
			title="Импорт из Google Контактов"
			hide-footer
			:no-close-on-backdrop="importApplying"
			:no-close-on-esc="importApplying"
		>
			<div v-if="importPreviewLoading" class="text-center text-muted py-5">
				<b-spinner class="mr-2"></b-spinner>
				Проверяю файл и сопоставляю сотрудников
			</div>
			<b-alert v-else-if="importError && !importPreview" show variant="danger">
				{{ importError }}
			</b-alert>
			<template v-else-if="importPreview">
				<b-alert show variant="info" class="py-2">
					Это предпросмотр: база данных ещё не изменена. Совпадение по имени является только подсказкой.
				</b-alert>
				<div class="employee-import-summary">
					<div>
						<span>Всего</span>
						<strong>{{ importPreview.summary.total }}</strong>
					</div>
					<div>
						<span>Надёжно сопоставлены</span>
						<strong class="text-success">{{ importPreview.summary.matched }}</strong>
					</div>
					<div>
						<span>Конфликты</span>
						<strong class="text-danger">{{ importPreview.summary.conflict }}</strong>
					</div>
					<div>
						<span>Без совпадения</span>
						<strong class="text-warning">{{ importPreview.summary.unmatched }}</strong>
					</div>
					<div>
						<span>Дубликаты файла</span>
						<strong>{{ importPreview.summary.duplicate_source }}</strong>
					</div>
				</div>
				<div class="employee-import-employer">
					<div>
						<strong>Справочник работодателей</strong>
						<div class="small text-muted">
							Создайте каноническое название один раз. Выбранные ниже варианты из Google сохранятся как его псевдонимы.
						</div>
					</div>
					<b-input-group size="sm">
						<b-form-input
							v-model.trim="newEmploymentOrganizationName"
							placeholder="Новая организация-работодатель"
							:disabled="employmentOrganizationCreating"
						></b-form-input>
						<b-input-group-append>
							<b-button
								variant="outline-primary"
								:disabled="!newEmploymentOrganizationName || employmentOrganizationCreating"
								@click="createImportEmploymentOrganization"
							>
								<b-spinner v-if="employmentOrganizationCreating" small></b-spinner>
								<span v-else>Создать</span>
							</b-button>
						</b-input-group-append>
					</b-input-group>
				</div>
				<b-alert v-if="importError" show variant="danger" class="py-2">
					{{ importError }}
				</b-alert>
				<div class="employee-import-table-wrap">
					<table class="table table-sm table-bordered employee-import-table">
						<thead>
							<tr>
								<th>Строка</th>
								<th>Контакт Google</th>
								<th>Результат проверки</th>
								<th>Решение</th>
								<th>Параметры</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in importPreview.rows" :key="row.decision_key">
								<td class="text-nowrap">{{ row.row_number }}</td>
								<td>
									<strong>{{ row.source_display_name }}</strong>
									<div v-if="row.canonical.organization_name" class="small text-muted">
										{{ row.canonical.organization_name }}
									</div>
									<div class="small text-muted">
										{{ importContactSummary(row) }}
									</div>
									<div v-for="warning in row.warnings" :key="warning.message" class="small text-warning">
										<i class="fas fa-exclamation-triangle mr-1"></i>{{ warning.message }}
									</div>
									<div
										v-for="issue in row.issues"
										:key="issue.code"
										class="small text-muted mt-1"
									>
										{{ issue.message }}
									</div>
								</td>
								<td>
									<b-badge :variant="importStatusVariant(row.status)">
										{{ importStatusLabel(row.status) }}
									</b-badge>
									<div v-if="row.matched_user" class="small mt-1">
										#{{ row.matched_user.user_id }} {{ row.matched_user.full_name }}
									</div>
									<div v-else-if="row.name_candidates && row.name_candidates.length" class="small text-muted mt-1">
										Подсказки:
										{{ row.name_candidates.map(item => `#${item.user_id} ${item.full_name}`).join('; ') }}
									</div>
								</td>
								<td>
									<b-form-select
										v-model="importDecisions[row.decision_key].action"
										size="sm"
										:options="importActionOptions(row)"
										@change="onImportActionChange(row)"
									></b-form-select>
									<b-form-select
										v-if="importDecisions[row.decision_key].action === 'match'"
										v-model="importDecisions[row.decision_key].user_id"
										size="sm"
										class="mt-1"
										:options="employeeOptionsForImportRow(row)"
									></b-form-select>
									<b-form-input
										v-if="importDecisions[row.decision_key].action === 'match' && row.status !== 'matched'"
										v-model.number="importDecisions[row.decision_key].user_id"
										type="number"
										min="1"
										size="sm"
										class="mt-1"
										placeholder="Или введите ID сотрудника"
									></b-form-input>
								</td>
								<td>
									<template v-if="importDecisions[row.decision_key].action !== 'ignore'">
										<b-form-select
											v-if="needsPrimaryPhoneDecision(row)"
											v-model="importDecisions[row.decision_key].primary_phone"
											size="sm"
											class="mb-1"
											:options="[{value: null, text: 'Выберите основной телефон'}].concat(primaryPhoneOptions(row))"
										></b-form-select>
										<b-form-select
											v-if="needsSystemGmailDecision(row)"
											v-model="importDecisions[row.decision_key].system_gmail"
											size="sm"
											class="mb-1"
											:options="[{value: null, text: 'Выберите системный Gmail'}].concat(systemGmailOptions(row))"
										></b-form-select>
										<b-form-select
											v-if="row.canonical.organization_name"
											v-model="importDecisions[row.decision_key].employment_organization_id"
											size="sm"
											:options="employmentOrganizationOptions"
											@change="onImportOrganizationChange(row)"
										></b-form-select>
									</template>
									<span v-else class="small text-muted">Строка не будет загружена.</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<b-form-checkbox
					v-model="importApplyConfirmed"
					:disabled="importApplying"
					class="mt-3"
				>
					Я проверил сопоставления. Применить изменения к основной базе сотрудников.
				</b-form-checkbox>
				<div class="d-flex flex-wrap justify-content-end mt-3">
					<b-button variant="outline-secondary" class="mr-2" :disabled="importApplying" @click="showImportModal = false">
						Закрыть без изменений
					</b-button>
					<b-button
						variant="success"
						:disabled="!canApplyImport || !importApplyConfirmed || importApplying"
						@click="applyGoogleContactsImport"
					>
						<b-spinner v-if="importApplying" small class="mr-1"></b-spinner>
						Применить импорт
					</b-button>
				</div>
			</template>
		</b-modal>
		<b-modal
			v-model="showSettingsModal"
			size="lg"
			title="Настройки контактных данных учётных записей"
			ok-title="Сохранить"
			cancel-title="Отмена"
			:busy="settingsSaving"
			@ok="saveSettings"
		>
			<div v-if="settingsLoading" class="text-center text-muted py-4">
				<b-spinner small class="mr-2"></b-spinner>
				Загрузка настроек
			</div>
			<div v-else>
				<section class="users-settings-section">
					<h6>Что сотрудник может менять сам</h6>
					<b-alert show variant="warning" class="py-2">
						Телефон влияет на авторизацию. По умолчанию самостоятельное изменение выключено.
					</b-alert>
					<b-form-checkbox
						v-for="field in selfEditFields"
						:key="field.key"
						v-model="settingsForm.self_edit_config[field.key]"
						switch
					>
						{{ field.label }}
						<small class="text-muted d-block">{{ field.hint }}</small>
					</b-form-checkbox>
				</section>

				<section class="users-settings-section">
					<h6>Видимость полей карточки</h6>
					<div class="users-settings-grid">
						<b-form-group
							v-for="field in visibilityFields"
							:key="field.key"
							:label="field.label"
						>
							<b-form-select
								v-model="settingsForm.visibility_config[field.key]"
								:options="visibilityOptions"
								size="sm"
							></b-form-select>
						</b-form-group>
					</div>
				</section>

				<section class="users-settings-section mb-0">
					<h6>Доступ к справочнику</h6>
					<b-alert show variant="secondary" class="py-2">
						Эти правила открывают список, карточки, редактирование и сами настройки справочника. ЛК, роли, активация и удаление остаются админскими действиями.
					</b-alert>
					<div class="users-access-rows">
						<div
							v-for="right in accessRights"
							:key="right.key"
							class="users-access-row"
						>
							<div class="users-access-row__main">
								<div class="users-access-row__title">{{ right.title }}</div>
								<div class="users-access-row__summary">{{ accessSummary(right.key) }}</div>
							</div>
							<b-button
								size="sm"
								:variant="expandedAccessRight === right.key ? 'primary' : 'outline-primary'"
								@click="toggleAccessRight(right.key)"
							>
								<i :class="expandedAccessRight === right.key ? 'fas fa-chevron-up' : 'fas fa-edit'" class="mr-1"></i>
								{{ expandedAccessRight === right.key ? 'Свернуть' : 'Изменить' }}
							</b-button>
							<div v-if="expandedAccessRight === right.key" class="users-access-row__editor">
								<MultipleParticipantsConfig
									:value="settingsForm.access_config[right.key]"
									:users="participantUsers"
									:departments="participantDepartments"
									:allowed-types="['list', 'list_all'].includes(right.key) ? ['specific_users', 'department'] : directoryAccessTypes"
									participant-label="участника"
									type-label="Тип доступа"
									all-text="Доступ получают все пользователи системы"
									:show-fallback-option="false"
									:show-priority="false"
									:empty-text="right.emptyText"
									:hint-text="right.hint"
									@input="value => onAccessConfigInput(right.key, value)"
								/>
							</div>
						</div>
					</div>
				</section>
			</div>
		</b-modal>
		</template>
	</div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import {returnToRow} from '@/utils/rowHighlight';
import {formatPhone, phoneHref} from '@/utils/phone';
import MultipleParticipantsConfig from '@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue';

const SESSION_KEY = 'userCardsListReturn';
const SESSION_TTL_MS = 20 * 60 * 1000;
const HIGHLIGHT_CLEANUP_DELAY_MS = 4500;


function cloneJson(value) {
	return JSON.parse(JSON.stringify(value || {}));
}

function getDefaultFilters() {
	return {
		q: '',
		status: 'all',
		cabinet: 'all',
		departmentId: 'all',
		roleId: 'all'
	};
}

function getDefaultSettingsForm() {
	return {
		self_edit_config: {
			avatar: true,
			avatar_source: true,
			position: false,
			gmail: false,
			phone: false,
			details: false
		},
		visibility_config: {
			avatar: 'all_authenticated',
			position: 'all_authenticated',
			departments: 'all_authenticated',
			phone: 'admin',
			telegram: 'admin',
			gmail: 'admin',
			details: 'admin',
			service: 'admin',
			history: 'admin'
		},
		access_config: {
			list: {participants: []},
			list_all: {participants: []},
			card: {participants: [{type: 'all'}]},
			edit: {participants: []},
			import_export: {participants: []},
			settings: {participants: []}
		}
	};
}

export default {
	name: "Users",
	components: {EmployeeLink,
		MultipleParticipantsConfig
	},
	data() {
		return {
			filters: getDefaultFilters(),
			mobileFiltersOpen: false,
			fields: [
				{
					key: 'user_id',
					label: 'ИД',
					sortable: true,
					tdClass: 'users-id-cell',
					thClass: 'users-id-cell'
				},
				{
					key: 'last_name',
					label: 'Сотрудник',
					sortable: true,
					class: 'users-person-col'
				},
				{
					key: 'phone',
					label: 'Контакты',
					class: 'users-contacts-col',
					sortable: true
				},
				{
					key: `active`,
					label: `Статус / ЛК`,
					sortable: true,
					class: 'users-status-col'
				},
				{
					key: `login`,
					label: `Доступ`,
					class: 'users-access-col',
					sortable: true
				},
				{
					key: 'departments',
					label: 'Отделы',
					class: 'users-departments-col',
					sortable: false
				},
				{key: 'two_factor', label: 'Вход по коду', class: 'users-two-factor-col'},
				{
					key: 'actions',
					label: '',
					tdClass: 'users-actions-cell',
					thClass: 'users-actions-cell'
				}
			],
			sortBy: 'last_name',
			sortDesc: false,
			perPage: 200,
			pageOptions: [5, 10, 15, 20, 100, 200],
			currentPage: 1,
			showTable: true,
			highlightCleanupTimer: null,
			highlightedUserId: null,
			restoringListContext: false,
			departmentOptions: [],
			showSettingsModal: false,
			settingsLoading: false,
			settingsSaving: false,
			directorySettings: null,
			exporting: false,
			showImportModal: false,
			importPreviewLoading: false,
			importApplying: false,
			importError: null,
			importFile: null,
			importPreview: null,
			importDecisions: {},
			importApplyConfirmed: false,
			employmentOrganizations: [],
			newEmploymentOrganizationName: '',
			employmentOrganizationCreating: false,
			settingsForm: getDefaultSettingsForm(),
			expandedAccessRight: null,
			directoryAccessTypes: ['all', 'specific_users', 'department'],
			accessRights: [
				{
					key: 'list',
					title: 'Список учётных записей',
					hint: 'Кто видит раздел «Учётные записи» и список активных аккаунтов.',
					emptyText: 'Список доступен только администраторам'
				},
				{
					key: 'list_all',
					title: 'Показывать отключённые учётные записи',
					hint: 'Кто может видеть отключённые аккаунты. Без этого права список ограничен активными учётными записями.',
					emptyText: 'Отключённые учётные записи видят только администраторы'
				},
				{
					key: 'card',
					title: 'Карточка учётной записи',
					hint: 'Кто может открыть системную карточку аккаунта или быстрый popover по имени.',
					emptyText: 'Карточки доступны только администраторам'
				},
				{
					key: 'edit',
					title: 'Редактирование учётных записей',
					hint: 'Кто может создавать и редактировать системные аккаунты. ЛК, роли и изменение статуса остаются отдельными административными действиями.',
					emptyText: 'Редактирование доступно только администраторам'
				},
				{
					key: 'import_export',
					title: 'Импорт и экспорт',
					hint: 'Кто может импортировать и экспортировать Google CSV. Импорт дополнительно требует редактирования и доступа ко всем сотрудникам; экспорт учитывает доступ к списку и карточкам.',
					emptyText: 'Импорт и экспорт доступны только администраторам'
				},
				{
					key: 'settings',
					title: 'Настройки справочника',
					hint: 'Кто может менять эти настройки доступа и видимости.',
					emptyText: 'Настройки доступны только администраторам'
				}
			],
			visibilityOptions: [
				{value: 'all_authenticated', text: 'Все авторизованные'},
				{value: 'users_read', text: 'Кто видит справочник'},
				{value: 'admin', text: 'Только администраторы'},
				{value: 'hidden', text: 'Скрыто'}
			],
			selfEditFields: [
				{key: 'avatar', label: 'Аватарка вручную', hint: 'Можно загрузить или удалить свою аватарку.'},
				{key: 'avatar_source', label: 'Источник аватарки', hint: 'Можно выбрать ручную аватарку или синхронизацию с Telegram.'},
				{key: 'position', label: 'Должность', hint: 'Безопасно только если должность не используется как право доступа.'},
				{key: 'gmail', label: 'Email', hint: 'Системный Gmail и дополнительные контактные адреса.'},
				{key: 'phone', label: 'Телефоны', hint: 'Основной номер влияет на Telegram-авторизацию. Включать только осознанно.'},
				{key: 'details', label: 'Подробные данные', hint: 'Отчество, дата рождения, организация, адреса, заметки и дополнительные поля.'}
			],
			visibilityFields: [
				{key: 'avatar', label: 'Аватарка'},
				{key: 'position', label: 'Должность'},
				{key: 'departments', label: 'Отделы'},
				{key: 'phone', label: 'Телефоны'},
				{key: 'telegram', label: 'Telegram'},
				{key: 'gmail', label: 'Email и системный Gmail'},
				{key: 'details', label: 'Подробные данные и адреса'},
				{key: 'service', label: 'Служебные поля'},
				{key: 'history', label: 'История изменений'}
			]
		}
	},
	computed: {
		canListAccounts() {
			return Boolean(this.directorySettings && this.directorySettings.can_list) || this.$store.getters.isAdmin === true;
		},
		users() {
			return this.$store.state.users || [];
		},
		departments() {
			return this.$store.state.departments || [];
		},
		canManageDirectorySettings() {
			return Boolean(this.directorySettings && this.directorySettings.can_manage) ||
				this.$store.getters.isAdmin === true;
		},
		canOpenEmployeeCards() {
			return Boolean(this.directorySettings && this.directorySettings.can_card) ||
				this.$can('read', 'users') ||
				this.$can('update', 'users');
		},
		canEditEmployees() {
			return this.canCreateEmployees || this.canUpdateEmployees;
		},
		canCreateEmployees() {
			return Boolean(this.directorySettings && this.directorySettings.can_edit) ||
				this.$can('create', 'users');
		},
		canUpdateEmployees() {
			return Boolean(this.directorySettings && this.directorySettings.can_edit) ||
				this.$can('update', 'users');
		},
		canImportEmployees() {
			return this.canImportExportEmployees && this.canEditEmployees && this.canListAllEmployees;
		},
		canExportEmployees() {
			return this.canImportExportEmployees && this.canOpenEmployeeCards;
		},
		canImportExportEmployees() {
			return Boolean(this.directorySettings && this.directorySettings.can_import_export) ||
				this.$store.getters.isAdmin === true;
		},
		canListAllEmployees() {
			return Boolean(this.directorySettings && this.directorySettings.can_list_all);
		},
		employmentOrganizationOptions() {
			return [
				{value: null, text: 'Организация: требуется решение'},
				{value: -1, text: 'Не переносить работодателя'}
			].concat(
				this.employmentOrganizations.map(item => ({
					value: item.id,
					text: item.short_name ? `${item.name} (${item.short_name})` : item.name
				}))
			);
		},
		canApplyImport() {
			if (!this.importPreview || !Array.isArray(this.importPreview.rows)) {
				return false;
			}
			return this.importPreview.rows.every(row => {
				const decision = this.importDecisions[row.decision_key];
				if (!decision || !decision.action) {
					return false;
				}
				if (row.status === 'duplicate_source') {
					return decision.action === 'ignore';
				}
				if (decision.action === 'ignore') {
					return true;
				}
				if (decision.action === 'match' && !Number(decision.user_id)) {
					return false;
				}
				if (
					decision.action === 'create'
					&& (!row.canonical.first_name || !row.canonical.last_name)
				) {
					return false;
				}
				if (
					row.canonical.organization_name
					&& (decision.employment_organization_id === null
						|| typeof decision.employment_organization_id === 'undefined')
				) {
					return false;
				}
				if (this.needsPrimaryPhoneDecision(row) && !decision.primary_phone) {
					return false;
				}
				if (this.needsSystemGmailDecision(row) && !decision.system_gmail) {
					return false;
				}
				return true;
			});
		},
		participantUsers() {
			return (this.users || []).map(user => ({
				value: Number(user.user_id || user.id),
				text: this.userFullName(user) || `#${user.user_id || user.id}`
			})).filter(user => user.value);
		},
		participantDepartments() {
			return (this.departments || []).map(department => ({
				value: Number(department.id),
				text: department.name
			})).filter(department => department.value);
		},
		roleOptions() {
			return (this.$store.state.roles || []).map(role => ({
				value: role.role_id,
				text: role.role_name
			}));
		},
		statusFilterOptions() {
			if (!this.canListAllEmployees) {
				return [
					{value: 'all', text: 'Действующие сотрудники'}
				];
			}
			return [
				{value: 'all', text: 'Все сотрудники'},
				{value: 'active', text: 'Действующие'},
				{value: 'inactive', text: 'Отключённые'}
			];
		},
		cabinetFilterOptions() {
			return [
				{value: 'all', text: 'Любой доступ'},
				{value: 'enabled', text: 'Есть ЛК'},
				{value: 'disabled', text: 'Нет ЛК'}
			];
		},
		departmentFilterOptions() {
			return [{value: 'all', text: 'Все отделы'}].concat(this.departmentOptions);
		},
		roleFilterOptions() {
			return [{value: 'all', text: 'Все роли'}].concat(this.roleOptions);
		},
		visibleUsers() {
			const words = this.getSearchWords(this.filters.q);
			return (this.users || [])
				.filter(user => this.matchesFilters(user, words))
				.slice()
				.sort(this.compareUsers);
		},
		totalRows() {
			return this.visibleUsers.length;
		},
		activeRowsCount() {
			return this.visibleUsers.filter(user => user.active).length;
		},
		cabinetRowsCount() {
			return this.visibleUsers.filter(user => user.cabinet_access_enabled).length;
		},
		activeFilterItems() {
			const items = [];
			if (this.filters.q) {
				items.push({
					key: 'q',
					text: `Поиск: ${this.filters.q}`
				});
			}
			if (this.filters.status !== 'all') {
				items.push({
					key: 'status',
					text: this.getOptionText(this.statusFilterOptions, this.filters.status)
				});
			}
			if (this.filters.cabinet !== 'all') {
				items.push({
					key: 'cabinet',
					text: this.getOptionText(this.cabinetFilterOptions, this.filters.cabinet)
				});
			}
			if (this.filters.departmentId !== 'all') {
				items.push({
					key: 'departmentId',
					text: this.getOptionText(this.departmentFilterOptions, this.filters.departmentId)
				});
			}
			if (this.filters.roleId !== 'all') {
				items.push({
					key: 'roleId',
					text: this.getOptionText(this.roleFilterOptions, this.filters.roleId)
				});
			}
			return items;
		}
	},
	beforeDestroy() {
		this.clearHighlightCleanupTimer();
		if (this._cancelUsersHighlight) this._cancelUsersHighlight();
		if (typeof window !== 'undefined') {
			window.removeEventListener('pageshow', this.handlePageShow);
		}
	},
	mounted() {
		if (typeof window !== 'undefined') {
			window.addEventListener('pageshow', this.handlePageShow);
		}
		this.fetchInitialData();
	},
	methods: {
		formatPhone,
		phoneHref,
		applyListContext(context) {
			if (!context) {
				return;
			}
			const page = Math.max(1, Number(context.page) || 1);
			this.restoringListContext = true;
			this.perPage = Number(context.perPage) || 200;
			this.sortBy = context.sortBy || 'last_name';
			this.sortDesc = Boolean(context.sortDesc);
			this.filters = Object.assign(getDefaultFilters(), cloneJson(context.filters));
			this.currentPage = page;
			this.$nextTick(() => {
				if (this._isDestroyed) {
					return;
				}
				this.currentPage = page;
				this.restoringListContext = false;
			});
		},
		saveReturnSession(userId) {
			try {
				sessionStorage.setItem(SESSION_KEY, JSON.stringify({
					userId: Number(userId),
					page: this.currentPage,
					perPage: this.perPage,
					filters: cloneJson(this.filters),
					sortBy: this.sortBy,
					sortDesc: this.sortDesc,
					savedAt: Date.now()
				}));
			} catch (e) { /* ignore */ }
		},
		readSession(userId) {
			try {
				const raw = sessionStorage.getItem(SESSION_KEY);
				if (!raw) {
					return null;
				}
				const data = JSON.parse(raw);
				if (
					Number(data.userId) === Number(userId) &&
					Date.now() - Number(data.savedAt || 0) < SESSION_TTL_MS
				) {
					return data;
				}
			} catch (e) { /* ignore */ }
			return null;
		},
		clearSession() {
			try {
				sessionStorage.removeItem(SESSION_KEY);
			} catch (e) { /* ignore */ }
		},
		clearHighlightCleanupTimer() {
			if (this.highlightCleanupTimer) {
				clearTimeout(this.highlightCleanupTimer);
				this.highlightCleanupTimer = null;
			}
		},
		scheduleHighlightCleanup(userId) {
			this.clearHighlightCleanupTimer();
			this.highlightCleanupTimer = setTimeout(() => {
				if (this._isDestroyed) {
					return;
				}
				this.highlightedUserId = null;
				const currentHighlight = this.$route.query && this.$route.query.highlight;
				if (!currentHighlight || String(currentHighlight) !== String(userId)) {
					return;
				}
				const nextQuery = Object.assign({}, this.$route.query);
				delete nextQuery.highlight;
				this.$router.replace({
					path: this.$route.path,
					query: nextQuery
				}).catch(() => {});
			}, HIGHLIGHT_CLEANUP_DELAY_MS);
		},
		isHighlightedUser(item) {
			return Boolean(item && this.highlightedUserId && Number(item.user_id) === Number(this.highlightedUserId));
		},
		resolveHighlightTarget(userId) {
			if (!this.$el) {
				return null;
			}
			const id = Number(userId);
			if (!Number.isInteger(id) || id <= 0) {
				return null;
			}
			const marker = this.$el.querySelector(`.users-table [data-user-id="${id}"]`);
			return marker
				? (marker.closest('tr') || marker)
				: this.$el.querySelector('.users-table .users-highlight-target');
		},
		async scrollToHighlight(userId) {
			if (this._cancelUsersHighlight) this._cancelUsersHighlight();
			this.highlightedUserId = Number(userId);
			const index = this.visibleUsers.findIndex(user => Number(user.user_id) === Number(userId));
			if (index < 0) return;
			this.currentPage = Math.floor(index / this.perPage) + 1;
			await this.$nextTick();
			await this.$nextTick();
			if (this._isDestroyed || this.highlightedUserId !== Number(userId)) return;
			this._cancelUsersHighlight = returnToRow(() => this.resolveHighlightTarget(userId), {
				container: target => {
					const body = target.closest('.b-table-sticky-header');
					return body && body.scrollHeight > body.clientHeight ? body : null;
				}
			});
		},
		async restoreFromHighlight(highlightId) {
			const userId = Number(highlightId);
			if (!Number.isInteger(userId) || userId <= 0) {
				return;
			}
			const session = this.readSession(userId);
			if (session) {
				this.clearSession();
				this.applyListContext(session);
			}
			await this.scrollToHighlight(userId);
			this.scheduleHighlightCleanup(userId);
		},
		handlePageShow() {
			const highlight = this.$route.query && this.$route.query.highlight;
			if (highlight) {
				this.scrollToHighlight(highlight);
			}
		},
		async fetchInitialData() {
			try {
				await this.loadDirectorySettings();
				if (!this.canListAccounts) return;
				await Promise.all([
					this.$store.dispatch('getAccountUsers', {full: true}),
					this.$store.dispatch('getDepartments'),
					this.$store.dispatch('getRoles')
				]);
			} catch (e) {
				this.$store.dispatch('showErrorModal', e);
			}
			const highlight = this.$route.query && this.$route.query.highlight;
			if (highlight) {
				await this.restoreFromHighlight(highlight);
			}
		},
		async loadDirectorySettings() {
			try {
				this.directorySettings = await this.$store.dispatch('getEmployeeDirectorySettings');
			} catch (e) {
				this.directorySettings = null;
			}
		},
		chooseGoogleContactsFile() {
			if (this.$refs.googleContactsImportInput) {
				this.$refs.googleContactsImportInput.click();
			}
		},
		async onGoogleContactsFileSelected(event) {
			const file = event && event.target && event.target.files ? event.target.files[0] : null;
			if (event && event.target) {
				event.target.value = '';
			}
			if (!file) {
				return;
			}
			this.importFile = file;
			this.importPreview = null;
			this.importDecisions = {};
			this.importApplyConfirmed = false;
			this.importError = null;
			this.showImportModal = true;
			this.importPreviewLoading = true;
			try {
				const results = await Promise.all([
					this.$store.dispatch('previewGoogleContactsImport', file),
					this.$store.dispatch('getEmploymentOrganizations', true).catch(() => [])
				]);
				this.importPreview = results[0];
				this.employmentOrganizations = results[1] || [];
				this.initializeImportDecisions();
			} catch (e) {
				this.importError = this.errorText(e, 'Не удалось проверить файл Google Контактов.');
			} finally {
				this.importPreviewLoading = false;
			}
		},
		initializeImportDecisions() {
			const decisions = {};
			(this.importPreview && this.importPreview.rows || []).forEach(row => {
				const decision = {
					action: null,
					user_id: null,
					primary_phone: null,
					system_gmail: null,
					employment_organization_id: null
				};
				if (row.status === 'matched' && row.matched_user && this.canUpdateEmployees) {
					decision.action = 'match';
					decision.user_id = row.matched_user.user_id;
				} else if (row.status === 'duplicate_source') {
					decision.action = 'ignore';
				}
				if (row.matched_employment_organization) {
					decision.employment_organization_id =
						row.matched_employment_organization.id;
				}
				decisions[row.decision_key] = decision;
			});
			this.importDecisions = decisions;
		},
		async createImportEmploymentOrganization() {
			if (!this.newEmploymentOrganizationName || this.employmentOrganizationCreating) {
				return;
			}
			this.employmentOrganizationCreating = true;
			this.importError = null;
			try {
				const created = await this.$store.dispatch('createEmploymentOrganization', {
					name: this.newEmploymentOrganizationName
				});
				this.employmentOrganizations = await this.$store.dispatch(
					'getEmploymentOrganizations',
					true
				);
				this.newEmploymentOrganizationName = '';
				this.$bvToast.toast(created.name, {
					title: 'Организация создана',
					variant: 'success',
					solid: true
				});
			} catch (e) {
				this.importError = this.errorText(e, 'Не удалось создать организацию.');
			} finally {
				this.employmentOrganizationCreating = false;
			}
		},
		importActionOptions(row) {
			if (row.status === 'duplicate_source') {
				return [{value: 'ignore', text: 'Игнорировать дубликат'}];
			}
			if (row.status === 'matched') {
				const result = [{value: 'ignore', text: 'Игнорировать'}];
				if (this.canUpdateEmployees) {
					result.unshift({value: 'match', text: 'Обновить найденного сотрудника'});
				}
				return result;
			}
			if (row.status === 'conflict') {
				const result = [
					{value: null, text: 'Требуется решение'},
					{value: 'ignore', text: 'Игнорировать'}
				];
				if (this.canUpdateEmployees) {
					result.splice(1, 0, {
						value: 'match',
						text: 'Привязать к выбранному сотруднику'
					});
				}
				return result;
			}
			const result = [
				{value: null, text: 'Требуется решение'},
				{value: 'ignore', text: 'Игнорировать'}
			];
			if (this.canUpdateEmployees) {
				result.splice(1, 0, {value: 'match', text: 'Привязать к существующему'});
			}
			if (
				this.canCreateEmployees
				&& row.canonical.first_name
				&& row.canonical.last_name
			) {
				result.splice(result.length - 1, 0, {
					value: 'create',
					text: 'Создать нового сотрудника'
				});
			}
			return result;
		},
		onImportActionChange(row) {
			const decision = this.importDecisions[row.decision_key];
			if (!decision) {
				return;
			}
			if (decision.action === 'match' && row.matched_user) {
				decision.user_id = row.matched_user.user_id;
			} else if (decision.action !== 'match') {
				decision.user_id = null;
			}
			this.importApplyConfirmed = false;
		},
		onImportOrganizationChange(row) {
			const decision = this.importDecisions[row.decision_key];
			const sourceName = this.normalizeSearchValue(row.canonical.organization_name);
			if (!decision || !sourceName) {
				return;
			}
			(this.importPreview.rows || []).forEach(otherRow => {
				if (this.normalizeSearchValue(otherRow.canonical.organization_name) !== sourceName) {
					return;
				}
				const otherDecision = this.importDecisions[otherRow.decision_key];
				if (otherDecision) {
					this.$set(
						otherDecision,
						'employment_organization_id',
						decision.employment_organization_id || null
					);
				}
			});
			this.importApplyConfirmed = false;
		},
		employeeOptionsForImportRow(row) {
			const candidates = [];
			if (row.matched_user) {
				candidates.push(row.matched_user);
			}
			(row.name_candidates || []).forEach(item => candidates.push(item));
			(row.identifier_evidence || []).forEach(item => {
				(item.user_ids || []).forEach(userId => {
					const user = (this.users || []).find(value => Number(value.user_id) === Number(userId));
					candidates.push(user || {
						user_id: userId,
						full_name: `Сотрудник #${userId}`
					});
				});
			});
			(this.users || []).forEach(item => candidates.push(item));
			const seen = new Set();
			const options = candidates.reduce((result, item) => {
				const userId = Number(item && item.user_id);
				if (!userId || seen.has(userId)) {
					return result;
				}
				seen.add(userId);
				result.push({
					value: userId,
					text: `#${userId} ${item.full_name || this.userFullName(item)}`
				});
				return result;
			}, []);
			return [{value: null, text: 'Выберите сотрудника'}].concat(options);
		},
		importTargetEmployee(row, userId) {
			const fromUsers = (this.users || []).find(item => Number(item.user_id) === Number(userId));
			if (fromUsers) {
				return fromUsers;
			}
			if (row.matched_user && Number(row.matched_user.user_id) === Number(userId)) {
				return row.matched_user;
			}
			return (row.name_candidates || []).find(item => Number(item.user_id) === Number(userId)) || null;
		},
		normalizeImportPhone(value) {
			const digits = String(value || '').replace(/\D/g, '');
			let normalized = digits;
			if (/^80\d{9}$/.test(digits)) {
				normalized = `375${digits.slice(2)}`;
			} else if (/^8\d{10}$/.test(digits)) {
				normalized = `7${digits.slice(1)}`;
			}
			return /^7\d{10}$/.test(normalized) || /^375\d{9}$/.test(normalized)
				? normalized
				: null;
		},
		primaryPhoneOptions(row) {
			const seen = new Set();
			return (row.canonical.contact_methods || [])
				.filter(item => item && item.contact_type === 'phone')
				.reduce((result, item) => {
					const value = this.normalizeImportPhone(item.value);
					if (!value || seen.has(value)) {
						return result;
					}
					seen.add(value);
					result.push({value, text: `${formatPhone(item.value)}${item.label ? ` · ${item.label}` : ''}`});
					return result;
				}, []);
		},
		needsPrimaryPhoneDecision(row) {
			const decision = this.importDecisions[row.decision_key] || {};
			if (!decision.action || decision.action === 'ignore') {
				return false;
			}
			const target = decision.action === 'match'
				? this.importTargetEmployee(row, decision.user_id)
				: null;
			return (!target || !target.phone) && this.primaryPhoneOptions(row).length > 1;
		},
		systemGmailOptions(row) {
			const seen = new Set();
			return (row.canonical.contact_methods || [])
				.filter(item => item && item.contact_type === 'email')
				.reduce((result, item) => {
					const value = String(item.value || '').trim().toLowerCase();
					if (!/^[a-z0-9](\.?[a-z0-9]){5,}@g(oogle)?mail\.com$/.test(value) || seen.has(value)) {
						return result;
					}
					seen.add(value);
					result.push({value, text: `${item.value}${item.label ? ` · ${item.label}` : ''}`});
					return result;
				}, []);
		},
		needsSystemGmailDecision(row) {
			const decision = this.importDecisions[row.decision_key] || {};
			if (!decision.action || decision.action === 'ignore') {
				return false;
			}
			const target = decision.action === 'match'
				? this.importTargetEmployee(row, decision.user_id)
				: null;
			return (!target || !target.gmail) && this.systemGmailOptions(row).length > 1;
		},
		importContactSummary(row) {
			const methods = row && row.canonical && Array.isArray(row.canonical.contact_methods)
				? row.canonical.contact_methods
				: [];
			const phones = methods.filter(item => item.contact_type === 'phone').length;
			const emails = methods.filter(item => item.contact_type === 'email').length;
			return `Телефонов: ${phones}; email: ${emails}`;
		},
		importStatusLabel(status) {
			const labels = {
				matched: 'Надёжное совпадение',
				conflict: 'Конфликт идентификаторов',
				unmatched: 'Не найден',
				duplicate_source: 'Дубликат в файле'
			};
			return labels[status] || status;
		},
		importStatusVariant(status) {
			const variants = {
				matched: 'success',
				conflict: 'danger',
				unmatched: 'warning',
				duplicate_source: 'secondary'
			};
			return variants[status] || 'secondary';
		},
		async applyGoogleContactsImport() {
			if (!this.canApplyImport || !this.importApplyConfirmed || this.importApplying) {
				return;
			}
			this.importApplying = true;
			this.importError = null;
			try {
				const result = await this.$store.dispatch('applyGoogleContactsImport', {
					file: this.importFile,
					fileSha256: this.importPreview.file_sha256,
					decisions: this.importDecisions
				});
				await this.$store.dispatch('getAccountUsers', {full: true});
				this.showImportModal = false;
				this.$bvToast.toast(
					`Обновлено: ${result.summary.matched}; создано: ${result.summary.created}; пропущено: ${result.summary.ignored}.` +
						(result.summary.skipped_organizations
							? ` Работодатель намеренно не перенесён: ${result.summary.skipped_organizations}.`
							: ''),
					{
						title: 'Импорт завершён',
						variant: 'success',
						solid: true
					}
				);
				this.importFile = null;
				this.importPreview = null;
				this.importDecisions = {};
				this.importApplyConfirmed = false;
			} catch (e) {
				this.importError = this.errorText(e, 'Не удалось применить импорт.');
			} finally {
				this.importApplying = false;
			}
		},
		async exportVisibleGoogleContacts() {
			if (this.exporting || !this.visibleUsers.length) {
				return;
			}
			this.exporting = true;
			try {
				const response = await this.$store.dispatch('exportGoogleContacts', {
					ids: this.visibleUsers.map(user => user.user_id).join(','),
					scope: this.visibleUsers.some(user => user.active === false) ? 'all' : 'active'
				});
				this.downloadBlob(
					response.data,
					`Сотрудники Аргус ${new Date().toISOString().slice(0, 10)}.csv`
				);
			} catch (e) {
				this.$store.dispatch('showErrorModal', e);
			} finally {
				this.exporting = false;
			}
		},
		downloadBlob(blob, fileName) {
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		},
		errorText(e, fallback) {
			const data = e && e.response && e.response.data;
			if (data && data.msg) {
				return data.msg;
			}
			if (typeof data === 'string' && data.trim()) {
				return data;
			}
			return e && e.message ? e.message : fallback;
		},
		openCreateCard() {
			this.$router.push({name: 'create-user'});
		},
		async prepareHighlightNavigation(userId) {
			this.saveReturnSession(userId);
			const currentHighlight = this.$route.query && this.$route.query.highlight;
			if (String(currentHighlight || '') === String(userId)) {
				return;
			}
			const nextQuery = Object.assign({}, this.$route.query, {highlight: String(userId)});
			await this.$router.replace({
				path: this.$route.path,
				query: nextQuery
			}).catch(() => {});
		},
		async openExistingUser(userId) {
			const id = Number(userId);
			if (!Number.isInteger(id) || id <= 0) {
				return;
			}
			await this.prepareHighlightNavigation(id);
			this.$router.push({
				name: 'user-card',
				params: {id: String(id)}
			}).catch(err => {
				if (!err || err.name !== 'NavigationDuplicated') {
					throw err;
				}
			});
		},
		onEdit(data) {
			return this.openExistingUser(data && data.item && data.item.user_id);
		},
		openUserCard(item) {
			if (item && item.user_id && this.canOpenEmployeeCards) {
				return this.openExistingUser(item.user_id);
			}
		},
		async openSettings() {
			this.showSettingsModal = true;
			this.settingsLoading = true;
			try {
				const settings = await this.$store.dispatch('getEmployeeDirectorySettings');
				this.directorySettings = settings;
				this.settingsForm = Object.assign(getDefaultSettingsForm(), settings || {});
				this.settingsForm.self_edit_config = Object.assign(
					getDefaultSettingsForm().self_edit_config,
					settings && settings.self_edit_config ? settings.self_edit_config : {}
				);
				this.settingsForm.visibility_config = Object.assign(
					getDefaultSettingsForm().visibility_config,
					settings && settings.visibility_config ? settings.visibility_config : {}
				);
				this.settingsForm.access_config = Object.assign(
					getDefaultSettingsForm().access_config,
					settings && settings.access_config ? settings.access_config : {}
				);
				this.settingsForm.access_config = this.normalizeAccessConfig(this.settingsForm.access_config);
			} catch (e) {
				this.$store.dispatch(`showErrorModal`, e);
			} finally {
				this.settingsLoading = false;
			}
		},
		async saveSettings(event) {
			if (event) {
				event.preventDefault();
			}
			this.settingsSaving = true;
			try {
				this.directorySettings = await this.$store.dispatch('updateEmployeeDirectorySettings', this.settingsForm);
				await this.$store.dispatch(`getAccountUsers`, { full: true });
				this.showSettingsModal = false;
			} catch (e) {
				this.$store.dispatch(`showErrorModal`, e);
			} finally {
				this.settingsSaving = false;
			}
		},
		normalizeAccessConfig(config) {
			const defaults = getDefaultSettingsForm().access_config;
			return {
				list: this.normalizeParticipantConfig(config && config.list, defaults.list),
				list_all: this.normalizeParticipantConfig(config && config.list_all, defaults.list_all),
				card: this.normalizeParticipantConfig(config && config.card, defaults.card),
				edit: this.normalizeParticipantConfig(config && config.edit, defaults.edit),
				import_export: this.normalizeParticipantConfig(config && config.import_export, defaults.import_export),
				settings: this.normalizeParticipantConfig(config && config.settings, defaults.settings)
			};
		},
		normalizeParticipantConfig(config, fallback) {
			if (typeof config === 'string') {
				if (config === 'all_authenticated') {
					return {participants: [{type: 'all'}]};
				}
				return {
					participants: [],
					legacy_level: config
				};
			}
			const source = config && typeof config === 'object' ? config : fallback;
			return {
				participants: Array.isArray(source && source.participants)
					? cloneJson(source.participants)
					: [],
				legacy_level: source && source.legacy_level ? source.legacy_level : undefined
			};
		},
		toggleAccessRight(key) {
			this.expandedAccessRight = this.expandedAccessRight === key ? null : key;
		},
		onAccessConfigInput(key, value) {
			this.$set(this.settingsForm.access_config, key, this.normalizeParticipantConfig(value, {participants: []}));
		},
		accessSummary(key) {
			const config = this.normalizeParticipantConfig(
				this.settingsForm && this.settingsForm.access_config ? this.settingsForm.access_config[key] : null,
				getDefaultSettingsForm().access_config[key]
			);
			const parts = [];
			if (config.legacy_level) {
				const legacy = this.getOptionText(this.visibilityOptions, config.legacy_level);
				if (legacy) {
					parts.push(`Старое правило: ${legacy}`);
				}
			}
			(config.participants || []).forEach(participant => {
				if (!participant || !participant.type) {
					return;
				}
				if (participant.type === 'all') {
					parts.push('Все пользователи');
				} else if (participant.type === 'specific_users') {
					const names = (participant.user_ids || [])
						.map(userId => this.userNameById(userId))
						.filter(Boolean);
					parts.push(names.length ? names.join(', ') : 'Конкретные пользователи не выбраны');
				} else if (participant.type === 'department') {
					parts.push(this.departmentNameById(participant.department_id) || 'Отдел не выбран');
				}
			});
			return parts.length ? parts.join('; ') : 'Только администраторы';
		},
		userNameById(userId) {
			const user = (this.users || []).find(item => Number(item.user_id || item.id) === Number(userId));
			return user ? this.userFullName(user) : null;
		},
		departmentNameById(departmentId) {
			const department = (this.departments || []).find(item => Number(item.id) === Number(departmentId));
			return department ? department.name : null;
		},
		userFullName(user) {
			if (!user) {
				return '';
			}
			return user.full_name || [user.last_name, user.first_name].filter(Boolean).join(' ').trim();
		},
		userInitials(user) {
			const first = user && user.first_name ? user.first_name.charAt(0) : '';
			const last = user && user.last_name ? user.last_name.charAt(0) : '';
			return `${last}${first}`.trim() || '?';
		},
		avatarUrl(user) {
			if (!user) {
				return null;
			}
			if (user.avatar_url) {
				return user.avatar_url;
			}
			if (user.has_avatar && user.user_id) {
				const stamp = user.avatar_updated_at ? new Date(user.avatar_updated_at).getTime() : Date.now();
				return `/api/users/${user.user_id}/avatar?v=${stamp}`;
			}
			return null;
		},
		formatRoles(roles) {
			return this.normalizedRoles(roles).map(role => role.name).join(', ');
		},
		normalizedRoles(roles) {
			if (!Array.isArray(roles) || !roles.length) {
				return [];
			}
			return roles
				.map(role => {
					if (role && typeof role === 'object') {
						return {
							id: role.id || role.role_id || role.value,
							name: role.name || role.role_name || role.text
						};
					}
					return {
						id: role,
						name: role
					};
				})
				.filter(role => role.name);
		},
		normalizedDepartments(departments) {
			if (!Array.isArray(departments) || !departments.length) {
				return [];
			}
			return departments
				.map(department => {
					if (department && typeof department === 'object') {
						return {
							id: department.id || department.department_id || department.value,
							name: department.name || department.text
						};
					}
					return {
						id: department,
						name: department
					};
				})
				.filter(department => department.name);
		},
		normalizeSearchValue(value) {
			return String(value || '')
				.toLowerCase()
				.replace(/ё/g, 'е')
				.replace(/\s+/g, ' ')
				.trim();
		},
		getSearchWords(query) {
			return this.normalizeSearchValue(query)
				.split(' ')
				.filter(Boolean);
		},
		getUserSearchText(user) {
			const departments = this.normalizedDepartments(user.departments).map(department => department.name).join(' ');
			const roles = this.normalizedRoles(user.roles).map(role => role.name).join(' ');
			const values = [
				user.user_id,
				user.last_name,
				user.first_name,
				user.phone,
				user.chat_id,
				user.login,
				user.gmail,
				departments,
				roles,
				user.active ? 'действующий' : 'отключён',
				user.cabinet_access_enabled ? 'личный кабинет лк' : 'нет лк'
			];
			return this.normalizeSearchValue(values.join(' '));
		},
		matchesFilters(user, words) {
			if (this.filters.status === 'active' && !user.active) {
				return false;
			}
			if (this.filters.status === 'inactive' && user.active) {
				return false;
			}
			if (this.filters.cabinet === 'enabled' && !user.cabinet_access_enabled) {
				return false;
			}
			if (this.filters.cabinet === 'disabled' && user.cabinet_access_enabled) {
				return false;
			}
			if (this.filters.departmentId !== 'all') {
				const hasDepartment = this.normalizedDepartments(user.departments)
					.some(department => String(department.id) === String(this.filters.departmentId));
				if (!hasDepartment) {
					return false;
				}
			}
			if (this.filters.roleId !== 'all') {
				const hasRole = this.normalizedRoles(user.roles)
					.some(role => String(role.id) === String(this.filters.roleId));
				if (!hasRole) {
					return false;
				}
			}
			if (!words.length) {
				return true;
			}
			const searchText = this.getUserSearchText(user);
			return words.every(word => searchText.indexOf(word) !== -1);
		},
		compareUsers(a, b) {
			const aActive = Boolean(a && a.active);
			const bActive = Boolean(b && b.active);
			if (aActive !== bActive) {
				return aActive ? -1 : 1;
			}
			const key = this.sortBy || 'last_name';
			const valueCompare = this.compareSortValue(this.getSortValue(a, key), this.getSortValue(b, key));
			if (valueCompare !== 0) {
				return this.sortDesc ? -valueCompare : valueCompare;
			}
			const lastNameCompare = this.compareSortValue(a && a.last_name, b && b.last_name);
			if (lastNameCompare !== 0) {
				return lastNameCompare;
			}
			const firstNameCompare = this.compareSortValue(a && a.first_name, b && b.first_name);
			if (firstNameCompare !== 0) {
				return firstNameCompare;
			}
			return this.compareSortValue(a && a.user_id, b && b.user_id);
		},
		getSortValue(user, key) {
			if (!user) {
				return '';
			}
			if (key === 'roles') {
				return this.formatRoles(user.roles);
			}
			if (key === 'departments') {
				return this.normalizedDepartments(user.departments).map(department => department.name).join(', ');
			}
			if (key === 'active' || key === 'cabinet_access_enabled') {
				return user[key] ? 1 : 0;
			}
			return user[key];
		},
		compareSortValue(a, b) {
			const aEmpty = a === null || typeof a === 'undefined' || a === '';
			const bEmpty = b === null || typeof b === 'undefined' || b === '';
			if (aEmpty && bEmpty) {
				return 0;
			}
			if (aEmpty) {
				return 1;
			}
			if (bEmpty) {
				return -1;
			}
			return String(a).localeCompare(String(b), 'ru', {
				numeric: true,
				sensitivity: 'base'
			});
		},
		getOptionText(options, value) {
			const option = (options || []).find(item => String(item.value) === String(value));
			return option ? option.text : value;
		},
		clearSearch() {
			this.filters.q = '';
		},
		clearFilterItem(key) {
			if (key === 'q') {
				this.filters.q = '';
				return;
			}
			if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
				this.filters[key] = 'all';
			}
		},
		resetFilters() {
			this.filters = getDefaultFilters();
		},
		onSortChanged(ctx) {
			this.sortBy = ctx && ctx.sortBy ? ctx.sortBy : 'last_name';
			this.sortDesc = Boolean(ctx && ctx.sortDesc);
			if (!this.restoringListContext) {
				this.currentPage = 1;
			}
		},
		rowClass(item) {
			if (!item) {
				return '';
			}
			return {
				'users-row--inactive': !item.active,
				'users-row--cabinet': item.active && item.cabinet_access_enabled,
				'users-row--no-cabinet': item.active && !item.cabinet_access_enabled,
				'users-row--clickable': this.canOpenEmployeeCards,
				'users-highlight-target': this.isHighlightedUser(item)
			};
		},
		rerenderTable() {
			this.showTable = false;
			this.$nextTick(() => this.showTable = true);
		}
	},
	watch: {
		perPage() {
			if (!this.restoringListContext) this.currentPage = 1;
		},
		departments: {
			handler(newVal) {
				if (Array.isArray(newVal) && newVal.length > 0) {
					this.departmentOptions = newVal.map(dept => ({
						value: dept.id,
						text: dept.name
					}));
				} else {
					this.departmentOptions = [];
				}
			},
			immediate: true
		},
		filters: {
			handler() {
				if (!this.restoringListContext) {
					this.currentPage = 1;
				}
			},
			deep: true
		}
	}
}
</script>

<style src="@/assets/rowHighlight.css"></style>
<style scoped>
.users-page {
	min-width: 0;
}

.users-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 10px;
}

.users-header__actions {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 6px;
}

.users-toolbar {
	display: grid;
	grid-template-columns: minmax(280px, 1.8fr) minmax(145px, .7fr) minmax(130px, .65fr) minmax(160px, .8fr) minmax(150px, .75fr);
	gap: 8px;
	align-items: center;
	margin-bottom: 8px;
}

.users-search {
	min-width: 0;
}

.users-filter-fields { display: contents; }
.users-filter-field { min-width: 0; margin: 0; }
.users-filter-field > span,
.users-mobile-filter-toggle,
.users-mobile-sort,
.users-mobile-page-size,
.users-mobile-id,
.users-mobile-pagination { display: none; }

.users-person__identity { min-width: 0; }
.users-person__name {
	display: block;
	padding: 0;
	border: 0;
	background: transparent;
	color: inherit;
	font: inherit;
	font-weight: 700;
	text-align: left;
	line-height: 1.3;
	overflow-wrap: anywhere;
}
.users-person__name:hover { color: #235a9b; }
.users-person__name:focus-visible { outline: 2px solid #235a9b; outline-offset: 3px; }

.employee-import-summary {
	display: grid;
	grid-template-columns: repeat(5, minmax(110px, 1fr));
	gap: 8px;
	margin-bottom: 12px;
}

.employee-import-summary > div {
	padding: 8px;
	border: 1px solid #e2e6ea;
	border-radius: 6px;
	background: #f8f9fa;
}

.employee-import-summary span,
.employee-import-summary strong {
	display: block;
}

.employee-import-summary span {
	color: #6c757d;
	font-size: 12px;
}

.employee-import-summary strong {
	font-size: 20px;
}

.employee-import-employer {
	display: grid;
	grid-template-columns: minmax(260px, 1fr) minmax(280px, 1fr);
	gap: 12px;
	align-items: end;
	margin-bottom: 12px;
	padding: 10px;
	border: 1px solid #dce6f3;
	border-radius: 6px;
	background: #f7fbff;
}

.employee-import-table-wrap {
	max-height: 58vh;
	overflow: auto;
}

.employee-import-table {
	min-width: 1180px;
	margin-bottom: 0;
	background: #fff;
}

.employee-import-table thead th {
	position: sticky;
	top: 0;
	z-index: 1;
	background: #f1f3f5;
}

.employee-import-table td {
	min-width: 145px;
	vertical-align: top;
}

.employee-import-table td:nth-child(2) {
	min-width: 230px;
}

.employee-import-table td:nth-child(4),
.employee-import-table td:nth-child(5) {
	min-width: 240px;
}

.users-active-filters {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 5px;
	margin: 4px 0 8px;
	padding: 6px 8px;
	border: 1px solid #ffe08a;
	border-radius: 5px;
	background: #fff8e1;
	color: #5c4300;
	font-size: 13px;
}

.users-active-filters__label {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	flex: 0 0 auto;
	font-weight: 700;
}

.users-active-filter,
.users-active-filters__reset {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	min-height: 28px;
	padding: 3px 8px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 700;
	line-height: 1.2;
	white-space: nowrap;
}

.users-active-filter {
	max-width: 100%;
	border: 1px solid #6c757d;
	background: #6c757d;
	color: #fff;
}

.users-active-filter__text {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.users-active-filters__reset {
	border: 1px solid #b38300;
	background: rgba(255, 255, 255, .72);
	color: #5c4300;
}

.users-table-meta {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	margin-bottom: 8px;
}

.users-table-controls {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	align-items: center;
	gap: 8px;
}

.users-page-size {
	min-width: 160px;
}

.users-table {
	margin-bottom: 0;
}

.users-table,
.users-table ::v-deep table {
	width: 100%;
	table-layout: fixed;
}

.users-table ::v-deep thead th {
	border-top: 1px solid #dee2e6;
	border-bottom-width: 1px;
	background: #f8f9fa;
	color: #495057;
	font-size: .78rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0;
	white-space: normal;
}

.users-table ::v-deep thead th,
.users-table ::v-deep tbody td {
	padding-right: .35rem;
	padding-left: .35rem;
	overflow-wrap: anywhere;
	vertical-align: middle;
	border-top-color: #edf0f2;
}

.users-table ::v-deep tbody tr:hover > td {
	background-color: #eef4ff;
}

.users-table ::v-deep tbody tr.users-row--inactive > td {
	background-color: #f8f9fa;
	color: #6c757d;
}

.users-table ::v-deep tbody tr.users-row--clickable {
	cursor: pointer;
}

.users-table ::v-deep tbody tr.users-row--cabinet > td:first-child {
	box-shadow: inset 4px 0 0 #28a745;
}

.users-table ::v-deep tbody tr.users-row--no-cabinet > td:first-child {
	box-shadow: inset 4px 0 0 #adb5bd;
}

.users-table ::v-deep .users-id-cell {
	width: 5%;
	white-space: nowrap;
}

.users-table ::v-deep .users-avatar-cell {
	width: 42px;
	min-width: 42px;
	text-align: center;
}

.users-avatar-mini {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	overflow: hidden;
	border-radius: 50%;
	background: #eef2f5;
	color: #495057;
	font-size: 11px;
	font-weight: 800;
	line-height: 1;
	vertical-align: middle;
}

.users-avatar-mini img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.users-table ::v-deep .users-actions-cell {
	width: 5%;
	white-space: nowrap;
}

.users-table ::v-deep .users-person-col { width: 19%; }
.users-table ::v-deep .users-contacts-col { width: 20%; }
.users-table ::v-deep .users-status-col { width: 11%; }
.users-table ::v-deep .users-access-col { width: 14%; }
.users-table ::v-deep .users-departments-col { width: 14%; }
.users-table ::v-deep .users-two-factor-col { width: 12%; }

.users-person {
	display: flex;
	min-width: 0;
	align-items: center;
	gap: 7px;
}

.users-person strong {
	min-width: 0;
	overflow-wrap: anywhere;
	line-height: 1.25;
}

.users-contact-stack,
.users-status-stack,
.users-access-stack {
	display: flex;
	min-width: 0;
	align-items: flex-start;
	flex-direction: column;
	gap: 3px;
}

.users-contact-stack > *,
.users-access-stack > * {
	max-width: 100%;
	overflow-wrap: anywhere;
}

.users-access-stack__roles {
	display: flex;
	flex-wrap: wrap;
}

.users-tag {
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	margin: 0 4px 4px 0;
	padding: 2px 7px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 700;
	line-height: 1.25;
	white-space: normal;
}

.users-tag--role {
	background: #e7f1ff;
	color: #084298;
}

.users-tag--department {
	background: #e9f7ef;
	color: #0f5132;
}

.users-actions {
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: flex-start;
}

.users-settings-section {
	padding: 10px;
	margin-bottom: 12px;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	background: #fff;
}

.users-settings-section h6 {
	margin-bottom: 10px;
	font-weight: 700;
}

.users-settings-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 10px;
}

.users-access-rows {
	display: grid;
	gap: 8px;
}

.users-access-row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 8px 10px;
	align-items: start;
	padding: 9px;
	border: 1px solid #edf0f2;
	border-radius: 6px;
	background: #f8f9fa;
}

.users-access-row__main {
	min-width: 0;
}

.users-access-row__title {
	color: #212529;
	font-weight: 700;
}

.users-access-row__summary {
	margin-top: 2px;
	color: #6c757d;
	font-size: 12px;
	overflow-wrap: anywhere;
}

.users-access-row__editor {
	grid-column: 1 / -1;
	min-width: 0;
	padding-top: 8px;
	border-top: 1px solid #e5e9ef;
}

@media (max-width: 1200px) {
	.users-toolbar {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
}

@media (max-width: 767.98px) {
	.users-page { padding-bottom: 20px !important; }
	.users-header h4 { font-size: 1.25rem; }
	.users-header__actions .btn,
	.users-search .btn,
	.users-toolbar .form-control,
	.users-toolbar .custom-select,
	.users-mobile-sort .btn {
		min-height: 44px;
		font-size: 16px;
	}
	.users-header__actions .btn { white-space: normal; }
	.users-mobile-filter-toggle {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 44px;
		font-size: 14px;
	}
	.users-filter-fields { display: none; }
	.users-filter-fields.is-open {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: 12px;
		padding: 12px;
		border: 1px solid #dce3eb;
		border-radius: 10px;
		background: #fff;
	}
	.users-filter-field > span,
	.users-mobile-sort,
	.users-mobile-page-size { display: block; }
	.users-filter-field > span,
	.users-mobile-sort > label,
	.users-mobile-page-size {
		margin-bottom: 4px;
		font-size: 13px;
		color: #526477;
	}
	.users-mobile-sort .custom-select { min-width: 0; }
	.users-mobile-sort .btn { min-width: 44px; }
	.users-mobile-page-size { margin: 0; }
	.users-mobile-page-size .custom-select { margin-top: 4px; }
	.users-active-filter,
	.users-active-filters__reset { min-height: 40px; }
	.users-mobile-pagination { display: flex; margin: 16px 0; }
	.users-page ::v-deep .pagination .page-link { min-height: 44px; display: grid; place-items: center; }
	.users-table {
		max-height: none !important;
		overflow: visible;
	}
	.users-table ::v-deep table.b-table.b-table-stacked-md { background: transparent; }
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 44px;
		gap: 8px;
		margin: 0 0 12px;
		padding: 12px;
		border: 1px solid #dce3eb;
		border-radius: 12px;
		background: #fff;
	}
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > td {
		grid-column: 1 / -1;
		width: 100%;
		min-width: 0;
		padding: 0;
		border: 0;
		background-color: transparent;
		text-align: left;
		font-size: 14px;
	}
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > [data-label]::before {
		display: block;
		float: none;
		width: 100%;
		padding: 0;
		margin-bottom: 3px;
		text-align: left;
		font-size: 12px;
		font-weight: 400;
		color: #66788a;
	}
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > [data-label] > div {
		display: block;
		width: 100%;
		padding: 0;
	}
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-id-cell,
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-person-col::before,
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-status-col::before,
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-contacts-col::before,
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-actions-cell::before { display: none; }
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-person-col { grid-column: 1; grid-row: 1; }
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-actions-cell { grid-column: 2; grid-row: 1; }
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-status-col { grid-row: 2; }
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-contacts-col { grid-row: 3; }
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-access-col { grid-row: 4; }
	.users-table ::v-deep table.b-table.b-table-stacked-md > tbody > tr > .users-departments-col { grid-row: 5; }
	.users-person { align-items: flex-start; gap: 10px; }
	.users-person__name { font-size: 16px; min-height: 40px; }
	.users-avatar-mini { flex: 0 0 38px; width: 38px; height: 38px; font-size: 13px; }
	.users-mobile-id { display: block; margin-top: 3px; color: #758393; font-size: 11px; }
	.users-status-stack { flex-direction: row; flex-wrap: wrap; align-items: center; gap: 8px; }
	.users-contact-stack a { display: inline-flex; align-items: center; min-height: 40px; font-size: 16px; }
	.users-contact-stack small { font-size: 13px; }
	.users-tag { max-width: 100%; white-space: normal; }
	.users-actions .btn { width: 44px; height: 44px; }
	.users-header {
		align-items: stretch;
		flex-direction: column;
	}

	.users-header__actions {
		justify-content: stretch;
	}

	.users-header__actions .btn {
		flex: 1 1 auto;
	}

	.users-toolbar {
		grid-template-columns: 1fr;
	}

	.users-active-filters {
		align-items: stretch;
	}

	.users-active-filters__label,
	.users-active-filters__reset {
		width: 100%;
	}

	.users-table-meta,
	.users-table-controls {
		align-items: stretch;
		flex-direction: column;
	}

	.users-page-size {
		display: none !important;
	}

	.users-settings-grid {
		grid-template-columns: 1fr;
	}

	.employee-import-summary {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.employee-import-employer {
		grid-template-columns: 1fr;
	}

	.users-access-row {
		grid-template-columns: 1fr;
	}
}
</style>
